import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

type SavedItem = {
    id: string;
    name: string;
    defaultAmount?: number | null;
    currency: string;
    updatedAt?: string;
};

const ItemsPage: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [items, setItems] = useState<SavedItem[]>([]);
    const [search, setSearch] = useState("");
    const [name, setName] = useState("");
    const [defaultAmount, setDefaultAmount] = useState("");
    const [currency, setCurrency] = useState("CAD");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const loadItems = useCallback(async (query = "") => {
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const url = query.trim()
                ? `${API_BASE_URL}/saveditems?q=${encodeURIComponent(query.trim())}`
                : `${API_BASE_URL}/saveditems`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Failed to load expenses.");
            const data = await response.json();
            setItems(Array.isArray(data) ? data : []);
        } catch (error: any) {
            toast.error(error?.message ?? "Unable to load expenses..");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        void loadItems();
    }, [loadItems]);

    const resetForm = () => {
        setEditingId(null);
        setName("");
        setDefaultAmount("");
        setCurrency("CAD");
    };

    const saveItem = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!name.trim()) return;
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/saveditems`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name: name.trim(),
                    defaultAmount: defaultAmount ? Number(defaultAmount) : null,
                    currency: currency.trim().toUpperCase() || "CAD",
                }),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok) throw new Error(data?.message ?? "Failed to save item.");
            toast.success(editingId ? "Expense updated." : "Expense saved.");
            resetForm();
            await loadItems(search);
        } catch (error: any) {
            toast.error(error?.message ?? "Unable to save expense.");
        }
    };

    const deleteItem = async (item: SavedItem) => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/saveditems/${item.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error("Failed to delete expense.");
            await loadItems(search);
        } catch (error: any) {
            toast.error(error?.message ?? "Unable to delete expense.");
        }
    };

    return (
        <div className="container py-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                    <h1 className="h3 mb-1">Frequently Used Expenses</h1>
                    <p className="text-muted mb-0">
                        Saved expense items appear automatically when you add expenses.
                    </p>
                </div>
            </div>

            <form onSubmit={saveItem} className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-5">
                            <label className="form-label">Item</label>
                            <input
                                className="form-control"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Hotel, Dinner, Gas..."
                                required
                            />
                        </div>
                        <div className="col-6 col-md-3">
                            <label className="form-label">Default amount</label>
                            <input
                                className="form-control"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={defaultAmount}
                                onChange={(e) => setDefaultAmount(e.target.value)}
                            />
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label">Currency</label>
                            <input
                                className="form-control"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
                                maxLength={3}
                                list="items-currency-options"
                            />
                            <datalist id="items-currency-options">
                                <option value="CAD" />
                                <option value="USD" />
                                <option value="EUR" />
                                <option value="GBP" />
                                <option value="TRY" />
                                <option value="JPY" />
                                <option value="AUD" />
                            </datalist>
                        </div>
                        <div className="col-12 col-md-2 d-flex gap-2">
                            <button className="btn btn-primary flex-grow-1" type="submit">
                                {editingId ? "Update" : "Save"}
                            </button>
                            {editingId && (
                                <button className="btn btn-outline-secondary" type="button" onClick={resetForm}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </form>

            <div className="mb-3">
                <input
                    className="form-control"
                    placeholder="Search expense..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        void loadItems(e.target.value);
                    }}
                />
            </div>

            {loading ? (
                <div className="text-muted">Loading...</div>
            ) : (
                <div className="list-group shadow-sm">
                    {items.map((item) => (
                        <div key={item.id} className="list-group-item d-flex justify-content-between align-items-center gap-3">
                            <div>
                                <strong>{item.name}</strong>
                                <div className="small text-muted">
                                    {item.defaultAmount != null ? `${item.currency} ${item.defaultAmount}` : item.currency}
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                        setEditingId(item.id);
                                        setName(item.name);
                                        setDefaultAmount(item.defaultAmount == null ? "" : String(item.defaultAmount));
                                        setCurrency(item.currency || "CAD");
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => void deleteItem(item)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && (
                        <div className="list-group-item text-muted">
                            No saved expenses yet.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ItemsPage;